import React from 'react'

import { createIntents } from 'component-library/typography/TextItem/helpers/create_intents'

import { TextLink } from 'component-library/typography/TextLink/TextLink'
import { LinkClickedEvent } from 'common/events/component-events'
import { Intention } from 'common/client-data-types/blocks'

export interface TextIntention {
    kind: 'text'
    value: string
    nestedIntents: HierarchicalIntent[]
}
export interface LinkIntention {
    kind: 'link'
    href: string
    nestedIntents: HierarchicalIntent[]
}
export interface EmphasizedIntention {
    kind: 'emphasized'
    nestedIntents: HierarchicalIntent[]
}
export interface ImportantIntention {
    kind: 'important'
    nestedIntents: HierarchicalIntent[]
}

export type HierarchicalIntent =
    | LinkIntention
    | EmphasizedIntention
    | ImportantIntention
    | TextIntention

export type ValidTextElements = 'p' | 'span' | 'li' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

export type OnEventHandler = (event: LinkClickedEvent) => void
export interface Props {
    text: string
    intentions?: Intention[]
    tag?: ValidTextElements
    className?: string
    openLinksInNewWindow?: boolean
    onEvent: OnEventHandler
}

const renderIntents = (
    intent: HierarchicalIntent,
    idx: number,
    onEvent?: OnEventHandler,
    openLinksInNewWindow?: boolean
) => {
    const { nestedIntents } = intent

    const renderIntentContent: () =>
        | string
        | React.ReactElement<any>
        | Array<React.ReactElement<any> | string> = () => {
        if (nestedIntents && nestedIntents.length > 0) {
            const renderedNestedIntents = nestedIntents.map((nestedIntent, i) =>
                renderIntents(nestedIntent, i, onEvent, openLinksInNewWindow)
            )
            return renderedNestedIntents.length === 1
                ? renderedNestedIntents[0]
                : renderedNestedIntents
        }
        return ''
    }

    switch (intent.kind) {
        case 'emphasized':
            return <em key={idx}>{renderIntentContent()}</em>
        case 'important':
            return <strong key={idx}>{renderIntentContent()}</strong>
        case 'link': {
            const { href } = intent

            return (
                <TextLink
                    key={idx}
                    href={href}
                    dataLinkType="article-inline"
                    openInNewWindow={openLinksInNewWindow}
                    onEvent={onEvent}
                >
                    {renderIntentContent()}
                </TextLink>
            )
        }
        case 'text':
        default:
            return intent.value
    }
}

export class TextItem extends React.PureComponent<Props, {}> {
    static displayName = 'TextItem'

    getIntents = () => {
        const { text, intentions } = this.props

        const intents = createIntents(intentions || [], text)

        return intents.map((intent, i) =>
            renderIntents(intent, i, this.props.onEvent, this.props.openLinksInNewWindow)
        )
    }

    render() {
        // Added to hack around React needing components that return more than one React component
        // to be wrapped in a container - TODO remove when React 16 is in use.
        const CustomTag = this.props.tag || 'p'
        const intents = this.getIntents()

        return (
            <CustomTag className={this.props.className}>
                {/* By returning a single element rather than an array, we don't output a react-text comment */}
                {intents.length === 1 ? intents[0] : intents}
            </CustomTag>
        )
    }
}
